import React, { useEffect } from "react";
import "../../assets/CRUDCourseAndLesson/CourseForm.css";
import Input from "../common/Input";
import ProgressTabs from "./ProgressTabs";
import CustomButton from "../common/CustomButton/CustomButton";
import { useNavigate, useLocation } from "react-router-dom";
import { getAllCategories as getAdminCategories } from "../../services/adminService";
import { getAllCategories as getInstructorCategories } from "../../services/instructorService";
import { toast } from "react-toastify";

const languageOptions = ["English", "Vietnamese"];
const levelOptions = ["Beginner", "Intermediate", "Advanced"];
const durationUnitOptions = ["Hours"];

const CourseForm = ({
  title = "Create New Course",
  onNext = () => {},
  onSaveDraft = () => {},
  initialData = {},
  completedTabs = [],
  onTabClick = () => {},
}) => {
  const [titleState, setTitle] = React.useState(initialData.title || "");
  const [subtitle, setSubtitle] = React.useState(
    initialData.subTitle || initialData.subtitle || ""
  );

  const [category, setCategory] = React.useState(initialData.category || "");
  const [subCategory, setSubCategory] = React.useState(
    initialData.subCategory || ""
  );
  const [language, setLanguage] = React.useState(initialData.language || "");
  const [subtitleLanguage, setSubtitleLanguage] = React.useState(
    initialData.subtitleLanguage || ""
  );
  const [level, setLevel] = React.useState(initialData.level || "");
  const [duration, setDuration] = React.useState(initialData.duration || "");
  const [durationUnit, setDurationUnit] = React.useState(
    initialData.durationUnit || "Hours"
  );
  const [price, setPrice] = React.useState(initialData.price || "");
  const [dataLoaded, setDataLoaded] = React.useState(false);

  // State for categories from database
  const [allCategories, setAllCategories] = React.useState([]);
  const [categoryMap, setCategoryMap] = React.useState({}); // Map category names to IDs
  const [loadingCategories, setLoadingCategories] = React.useState(false);
  const [categoryError, setCategoryError] = React.useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // Detect if user is admin or instructor based on route
  const isAdmin = location.pathname.includes("/admin/");
  const isInstructor = location.pathname.includes("/instructor/");

  // Language conversion mapping
  const languageMapping = {
    English: "english",
    Vietnamese: "vietnam",
  };

  // Convert display language to backend format
  const convertLanguageToBackend = (displayLanguage) => {
    return languageMapping[displayLanguage] || displayLanguage;
  };

  // Convert backend language to display format
  const convertLanguageToDisplay = (backendLanguage) => {
    const reverseMapping = Object.fromEntries(
      Object.entries(languageMapping).map(([key, value]) => [value, key])
    );
    return reverseMapping[backendLanguage] || backendLanguage;
  };

  // Fetch categories from database
  const fetchCategories = async () => {
    setLoadingCategories(true);
    setCategoryError("");
    try {
      // Use appropriate service based on user role
      const getAllCategories = isInstructor ? getInstructorCategories : getAdminCategories;
      const response = await getAllCategories();

      if (response.data && response.data.success) {
        const categories = response.data.data.map((cat) => cat.name);
        setAllCategories(categories);

        // Create mapping from category name to ID
        const categoryIdMap = {};
        response.data.data.forEach((cat) => {
          categoryIdMap[cat.name] = cat._id;
        });
        setCategoryMap(categoryIdMap);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoryError("Failed to load categories from database");
      toast.error("Failed to load categories from database");
      // Set empty array if API fails
      setAllCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle category ID to name mapping when categories are loaded
  useEffect(() => {
    if (
      allCategories.length > 0 &&
      categoryMap &&
      Object.keys(categoryMap).length > 0
    ) {
      // If we have category/subcategory IDs but no names, try to map them
      if (initialData.categoryId && !initialData.category) {
        // Find category name by ID
        const categoryName = Object.keys(categoryMap).find(
          (key) => categoryMap[key] === initialData.categoryId
        );
        if (categoryName) {
          setCategory(categoryName);
        }
      }

      if (initialData.subCategoryId && !initialData.subCategory) {
        // Find subcategory name by ID
        const subCategoryName = Object.keys(categoryMap).find(
          (key) => categoryMap[key] === initialData.subCategoryId
        );
        if (subCategoryName) {
          setSubCategory(subCategoryName);
        }
      }
    }
  }, [
    allCategories,
    categoryMap,
    initialData.categoryId,
    initialData.subCategoryId,
    initialData.category,
    initialData.subCategory,
  ]);

  // Filter categories for dropdowns - exclude selected values
  const categoryOptions = allCategories.filter((cat) => cat !== subCategory);
  const subCategoryOptions = allCategories.filter((cat) => cat !== category);

  // Handle category change
  const handleCategoryChange = (val) => {
    setCategory(val);
    // Clear subcategory if it's the same as the new category
    if (val === subCategory) {
      setSubCategory("");
    }
  };

  // Handle subcategory change
  const handleSubCategoryChange = (val) => {
    setSubCategory(val);
    // Clear category if it's the same as the new subcategory
    if (val === category) {
      setCategory("");
    }
  };

  // Update state when initialData changes
  useEffect(() => {
    // Set loading to false first
    setDataLoaded(false);

    // Always set from initialData, even if empty string
    setTitle(initialData.title || "");
    setSubtitle(initialData.subTitle || initialData.subtitle || "");

    // Handle category restoration - prioritize category names over IDs
    setCategory(initialData.category || "");
    setSubCategory(initialData.subCategory || "");

    // Handle language and level restoration - prioritize display format
    // If we have display format data, use it directly
    if (
      initialData.language &&
      !initialData.language.includes("english") &&
      !initialData.language.includes("vietnam")
    ) {
      setLanguage(initialData.language);
    } else {
      // Convert backend language format to display format
      setLanguage(convertLanguageToDisplay(initialData.language || ""));
    }

    if (
      initialData.subtitleLanguage &&
      !initialData.subtitleLanguage.includes("english") &&
      !initialData.subtitleLanguage.includes("vietnam")
    ) {
      setSubtitleLanguage(initialData.subtitleLanguage);
    } else {
      setSubtitleLanguage(
        convertLanguageToDisplay(initialData.subtitleLanguage || "")
      );
    }

    // Handle level restoration - prioritize display format
    if (
      initialData.level &&
      !initialData.level.includes("beginner") &&
      !initialData.level.includes("intermediate") &&
      !initialData.level.includes("advanced")
    ) {
      setLevel(initialData.level);
    } else {
      // Convert backend level format to display format
      const levelValue = initialData.level || "";
      if (levelValue) {
        const levelMap = {
          beginner: "Beginner",
          intermediate: "Intermediate",
          advanced: "Advanced",
        };
        const displayLevel = levelMap[levelValue.toLowerCase()] || levelValue;
        setLevel(displayLevel);
      } else {
        setLevel("");
      }
    }

    // Parse duration to extract number part (e.g., "123 Hours" -> "123")
    const durationValue = initialData.duration
      ? initialData.duration.toString().split(" ")[0]
      : "";
    setDuration(durationValue);
    setDurationUnit("Hours");
    setPrice(initialData.price || "");

    // Use setTimeout to ensure all state updates are complete
    setTimeout(() => {
      setDataLoaded(true);
    }, 50);
  }, [initialData]);

  const buildCourseData = () => {
    // Collect all valid category IDs
    const categoryIds = [];
    if (categoryMap && Object.keys(categoryMap).length > 0) {
      if (category && category !== "Select..." && categoryMap[category]) {
        categoryIds.push(categoryMap[category]);
      }
      if (subCategory && subCategory !== "Select..." && categoryMap[subCategory]) {
        categoryIds.push(categoryMap[subCategory]);
      }
    }

    const data = {
      title: titleState,
      subTitle: subtitle,
      detail: {
        description: "No description provided", // Required by backend
        willLearn: [], // Optional array
        targetAudience: [], // Optional array
        requirement: [], // Optional array
      },
      materials: [], // Optional array
      categoryIds, // Send array of category IDs
      price: parseFloat(price) || 0,
      level: level?.toLowerCase() || "beginner",
      duration: duration ? `${duration} ${durationUnit}` : "",
      language: convertLanguageToBackend(language) || "vietnam",
      subtitleLanguage: convertLanguageToBackend(subtitleLanguage) || "vietnam",
      sections: [], // Will be populated later

      // Save display values for form restoration
      category,
      subCategory,
      languageDisplay: language,
      subtitleLanguageDisplay: subtitleLanguage,
      levelDisplay: level,

      // Save individual category IDs for backward compatibility
      categoryId: categoryIds[0] || null,
      subCategoryId: categoryIds[1] || null,
    };

    return data;
  };

  const handleSaveNext = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Validate categories
    if (!categoryMap || Object.keys(categoryMap).length === 0) {
      toast.error("Categories not loaded. Please try again.");
      return;
    }

    // Collect all valid category IDs
    const categoryIds = [];
    if (category && category !== "Select..." && categoryMap[category]) {
      categoryIds.push(categoryMap[category]);
    }
    if (subCategory && subCategory !== "Select..." && categoryMap[subCategory]) {
      categoryIds.push(categoryMap[subCategory]);
    }

    // Validate that we have at least one valid category
    if (categoryIds.length === 0) {
      toast.error("Please select at least one valid category");
      return;
    }

    const data = buildCourseData();
    onNext(data);
  };

  const handleSaveDraft = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // For draft, we only require title
    if (!titleState || titleState.trim() === "") {
      toast.error("At least a title is required to save as draft");
      return;
    }

    const data = buildCourseData();
    onSaveDraft(data);
  };

  const handleCancel = () => {
    // Navigate to appropriate courses page based on user role
    if (isInstructor) {
      navigate("/instructor/courses");
    } else {
      navigate("/admin/courses/all");
    }
  };

  const allFieldsFilled =
    titleState?.trim() &&
    subtitle?.trim() &&
    category &&
    category !== "Select..." &&
    subCategory &&
    subCategory !== "Select..." &&
    language &&
    language !== "Select..." &&
    level &&
    level !== "Select..." &&
    duration &&
    price;

  return (
    <div className="cf-app-container">
      <div className="cf-content-area">
        <div className="cf-main-content">
          <ProgressTabs
            activeIndex={0}
            completedTabs={completedTabs}
            onTabClick={onTabClick}
          />
          <div className="cf-form-content">
            <div className="cf-form-header">
              <h2 className="cf-form-title">{title}</h2>
            </div>

            {/* Show category error if any */}
            {categoryError && (
              <div
                style={{
                  color: "red",
                  marginBottom: "16px",
                  padding: "8px 12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                }}
              >
                {categoryError}
              </div>
            )}

            <form className="cf-course-form" onSubmit={handleSaveNext}>
              <div className="cf-form-group">
                <label htmlFor="title" className="cf-form-label">
                  Title
                </label>
                <div className="cf-input-container">
                  <Input
                    id="title"
                    placeholder="Your course title"
                    maxLength={80}
                    value={titleState}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
              </div>
              <div className="cf-form-group">
                <label htmlFor="subtitle" className="cf-form-label">
                  Subtitle
                </label>
                <div className="cf-input-container">
                  <Input
                    id="subtitle"
                    placeholder="Your course subtitle"
                    maxLength={120}
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                  />
                </div>
              </div>

              <div className="cf-form-row">
                <div className="cf-form-group">
                  <label className="cf-form-label">Course Category</label>
                  <Input
                    variant="dropdown"
                    options={categoryOptions}
                    value={category}
                    text={
                      loadingCategories ? "Loading..." : category || "Select..."
                    }
                    onChange={handleCategoryChange}
                    disabled={loadingCategories}
                  />
                </div>
                <div className="cf-form-group">
                  <label className="cf-form-label">Course Sub-category</label>
                  <Input
                    variant="dropdown"
                    options={subCategoryOptions}
                    value={subCategory}
                    text={
                      loadingCategories
                        ? "Loading..."
                        : subCategory || "Select..."
                    }
                    onChange={handleSubCategoryChange}
                    disabled={loadingCategories}
                  />
                </div>
              </div>
              <div className="cf-form-row">
                <div className="cf-form-group">
                  <label className="cf-form-label">Course Language</label>
                  <Input
                    variant="dropdown"
                    options={languageOptions}
                    value={language}
                    text={language || "Select..."}
                    onChange={(val) => setLanguage(val)}
                  />
                </div>
                <div className="cf-form-group">
                  <label className="cf-form-label">
                    Subtitle Language (Optional)
                  </label>
                  <Input
                    variant="dropdown"
                    options={languageOptions}
                    value={subtitleLanguage}
                    text={subtitleLanguage || "Select..."}
                    onChange={(val) => setSubtitleLanguage(val)}
                  />
                </div>
              </div>
              <div className="cf-form-row-3">
                <div className="cf-form-group">
                  <label className="cf-form-label">Course Level</label>
                  <Input
                    variant="dropdown"
                    options={levelOptions}
                    value={level}
                    text={level || "Select..."}
                    onChange={(val) => setLevel(val)}
                  />
                </div>
                <div className="cf-form-group cf-small-input">
                  <label className="cf-form-label">Durations</label>
                  <div className="cf-duration-container">
                    <Input
                      placeholder="Course durations"
                      type="number"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                    />
                    <span style={{ marginLeft: 8, lineHeight: "32px" }}>
                      Hours
                    </span>
                  </div>
                </div>
                <div className="cf-form-group cf-price-input">
                  <label htmlFor="price" className="cf-form-label">
                    Course Price
                  </label>
                  <div className="cf-input-container">
                    <Input
                      id="price"
                      placeholder="Enter course price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="acc-navigation-buttons">
                <CustomButton
                  color="transparent"
                  type="normal"
                  size="large"
                  onClick={handleCancel}
                >
                  Cancel
                </CustomButton>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <CustomButton
                    color="primary"
                    type="normal"
                    size="large"
                    onClick={handleSaveDraft}
                    disabled={!titleState || titleState.trim() === ""}
                  >
                    Save to Draft
                  </CustomButton>
                  <CustomButton
                    color="primary"
                    type="normal"
                    size="large"
                    onClick={handleSaveNext}
                    disabled={!allFieldsFilled}
                  >
                    Next
                  </CustomButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseForm;
